import { toast, toastifyToast } from 'component-library';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { usePostDraftPlotCommitAsync } from '@/api/rest/resources/plot';
import { Logger } from '@/lib/logs/logger';

import { usePlotPaths } from './usePlotPaths';

export const COMMIT_TOAST_ID = 'draft-plot-commit';

type UseDraftPlotCommitParams = {
  organizationId: string | null;
  panelState: string;
  hasDraftPlots: boolean;
};

export const useDraftPlotCommit = ({ organizationId, panelState, hasDraftPlots }: UseDraftPlotCommitParams) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutateAsync: commitAsync, isPending: isCommitting } = usePostDraftPlotCommitAsync();
  const { plots } = usePlotPaths();

  const plotsPageUrl = `${plots}?organizationId=${organizationId}`;
  const canCommit = panelState === 'review' && hasDraftPlots;

  // draftPlotIds is the specific set to commit (a selection, or a whole group) - callers decide which.
  const handleCommit = (draftPlotIds: string[]) => {
    toastifyToast.clearWaitingQueue();
    toastifyToast.dismiss();

    commitAsync({ bodyData: { draft_plots: draftPlotIds } })
      .then((response) => {
        toast({
          title: t('shared.uploadPlots.commit.creating', { count: draftPlotIds.length }),
          type: 'success',
          autoClose: false,
          toastId: COMMIT_TOAST_ID,
        });
        navigate(plotsPageUrl, { state: { commitTaskId: response.task_id, commitCount: draftPlotIds.length } });
      })
      .catch((error) => {
        Logger.errorOnce(error as Error);
        toast({ title: t('shared.uploadPlots.commit.failed'), type: 'error', autoClose: 10_000 });
      });
  };

  const handleSaveDraftPlots = () => {
    navigate(plotsPageUrl);
  };

  return { handleCommit, handleSaveDraftPlots, isCommitting, canCommit };
};
